import { Link } from "react-router-dom";
import { ArrowRight, Award, Brain, HeartHandshake, Shield, Sparkles, Phone, Video } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import {
  PRACTITIONER,
  IMAGES,
  AREAS,
  METHODS,
  TESTIMONIALS,
  FAQ,
} from "@/data/site";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const benefits = [
  {
    icon: Brain,
    title: "Approche intégrative",
    text: "Une lecture clinique structurée et plusieurs méthodes validées, choisies selon votre situation.",
  },
  {
    icon: Shield,
    title: "Cadre éthique",
    text: "Confidentialité, déontologie, accompagnement respectueux de votre rythme et de vos choix.",
  },
  {
    icon: HeartHandshake,
    title: "Présence humaine",
    text: "Une écoute chaleureuse et précise, dans un cabinet apaisé à Paris 16e ou en visioconsultation.",
  },
  {
    icon: Sparkles,
    title: "Outils innovants",
    text: "EMDR, hypnose, EFT, cohérence cardiaque, mindfulness : des leviers concrets pour avancer.",
  },
];

export default function Home() {
  return (
    <div data-testid="page-home" className="page-enter">
      <SEO
        title="François-Xavier Villette — Psychologue & Psychothérapeute Paris 16e"
        description="Psychologue et psychothérapeute à Paris 16e. Approche intégrative pour anxiété, burn-out, psychotraumatismes. Cabinet et visioconsultation."
      />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative pt-32 md:pt-40 pb-20 md:pb-32 bg-[#FAFAFA] grain overflow-hidden"
      >
        <div className="container-fxv grid lg:grid-cols-12 gap-12 lg:gap-16 items-center relative z-10">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="overline">
                <span className="gold-line">Cabinet à Paris 16e · Visioconsultation</span>
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h1
                data-testid="hero-headline"
                className="font-serif text-[#0C2340] text-5xl md:text-6xl lg:text-7xl leading-[1.05] mt-6"
              >
                Retrouvez un <em className="not-italic text-[#C5A059]">équilibre</em>
                <br />
                émotionnel durable.
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-lg text-[#4A5568] max-w-xl leading-relaxed">
                Psychologue et psychothérapeute à Paris 16e et en
                visioconsultation. Une approche intégrative qui allie rigueur
                clinique et méthodes innovantes — EMDR, hypnose, EFT, mindfulness — pour
                vous accompagner durablement.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="rounded-full bg-[#0C2340] text-white px-8 py-4 hover:bg-[#163861] transition-colors font-medium inline-flex items-center gap-2"
                >
                  Prendre rendez-vous
                  <ArrowRight size={18} strokeWidth={1.5} />
                </Link>
                <a
                  href={`tel:${PRACTITIONER.phone}`}
                  data-testid="hero-cta-call"
                  className="rounded-full border border-[#C5A059] text-[#0C2340] px-8 py-4 hover:bg-[#C5A059] hover:text-white transition-colors font-medium inline-flex items-center gap-2"
                >
                  <Phone size={16} strokeWidth={1.5} />
                  Être rappelé
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl border-t border-[#E2E8F0] pt-8">
                {[
                  { k: "15+", v: "années de pratique" },
                  { k: "10", v: "approches intégrées" },
                  { k: "100%", v: "confidentialité" },
                ].map((s) => (
                  <div key={s.v}>
                    <p className="font-serif text-3xl text-[#0C2340]">{s.k}</p>
                    <p className="text-xs uppercase tracking-[0.18em] text-[#4A5568] mt-1">
                      {s.v}
                    </p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5 relative">
            <Reveal delay={0.1}>
              <div className="relative aspect-[4/5] w-full max-w-[480px] mx-auto">
                <div className="absolute -top-6 -left-6 w-32 h-32 border border-[#C5A059] z-0" />
                <img
                  src={IMAGES.portrait}
                  alt="Portrait de François-Xavier Villette, psychologue"
                  className="relative z-10 w-full h-full object-cover"
                />
                <div className="absolute -bottom-6 -right-6 w-48 h-32 bg-[#0C2340] z-0 flex items-center justify-center text-white text-center px-4">
                  <div>
                    <p className="font-serif text-xl leading-tight">
                      Une parole précise.
                    </p>
                    <p className="text-xs tracking-[0.15em] uppercase text-[#C5A059] mt-1">
                      Un cadre sûr
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DIFFICULTIES */}
      <section
        data-testid="difficulties-section"
        className="section-fxv bg-white"
      >
        <div className="container-fxv">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <Reveal>
                <p className="overline gold-line">Domaines d'accompagnement</p>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-serif text-4xl md:text-5xl text-[#0C2340] mt-6 leading-tight">
                  Les difficultés que nous traversons ensemble
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <Reveal delay={0.1}>
                <p className="text-[#4A5568] leading-relaxed text-lg">
                  Chaque parcours est singulier. L'accompagnement est
                  personnalisé, en cabinet à Paris 16e ou en visioconsultation,
                  pour adultes en demande d'un soutien rigoureux et humain.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {AREAS.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.05}>
                <Link
                  to={`/domaines/${a.slug}`}
                  data-testid={`difficulty-card-${a.slug}`}
                  className="group bg-white p-10 h-full flex flex-col justify-between hover:bg-[#FAFAFA] transition-colors"
                >
                  <div>
                    <p className="overline">{String(i + 1).padStart(2, "0")}</p>
                    <h3 className="font-serif text-2xl text-[#0C2340] mt-4 leading-snug">
                      {a.title}
                    </h3>
                    <p className="text-sm text-[#4A5568] mt-4 leading-relaxed line-clamp-4">
                      {a.intro}
                    </p>
                  </div>
                  <span className="mt-8 inline-flex items-center gap-2 text-sm text-[#0C2340] group-hover:text-[#C5A059] transition-colors">
                    Découvrir
                    <ArrowRight size={16} strokeWidth={1.5} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* METHODS */}
      <section
        data-testid="methods-section"
        className="section-fxv bg-[#F0F4F8]"
      >
        <div className="container-fxv">
          <div className="text-center max-w-2xl mx-auto">
            <Reveal>
              <p className="overline">
                <span className="gold-line">Méthodes thérapeutiques</span>
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-4xl md:text-5xl text-[#0C2340] mt-6">
                Dix approches au service d'un seul objectif : vous
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-[#4A5568] mt-6 leading-relaxed">
                Plutôt que d'imposer une seule méthode, l'accompagnement
                s'appuie sur dix approches reconnues et complémentaires,
                choisies en fonction de votre situation et de votre rythme.
              </p>
            </Reveal>
          </div>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {METHODS.map((m, i) => (
              <Reveal key={m.slug} delay={i * 0.03}>
                <Link
                  to={`/methodes/${m.slug}`}
                  data-testid={`method-tile-${m.slug}`}
                  className="block bg-white border border-[#E2E8F0] p-6 hover:border-[#C5A059] hover:-translate-y-1 transition-all h-full"
                >
                  <p className="font-serif text-xl text-[#0C2340]">{m.name}</p>
                  <p className="text-xs text-[#4A5568] mt-3 leading-relaxed line-clamp-3">
                    {m.objectif}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section
        data-testid="about-teaser-section"
        className="section-fxv bg-white"
      >
        <div className="container-fxv grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative">
            <img
              src={IMAGES.office}
              alt="Cabinet de consultation"
              className="w-full aspect-[4/5] object-cover"
            />
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <p className="overline gold-line">Le praticien</p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-4xl md:text-5xl text-[#0C2340] mt-6">
                Une exigence clinique au service de votre équilibre
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-[#4A5568] mt-6 leading-relaxed">
                Psychologue diplômé et psychothérapeute, François-Xavier
                Villette accompagne adultes et professionnels confrontés à
                l'anxiété, au burn-out, aux psychotraumatismes et aux
                transitions de vie. Sa pratique mêle rigueur scientifique et
                approches intégratives innovantes.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 grid sm:grid-cols-2 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
                {benefits.map((b) => (
                  <div
                    key={b.title}
                    className="bg-white p-6 flex gap-4 items-start"
                  >
                    <b.icon
                      size={22}
                      strokeWidth={1.5}
                      className="text-[#C5A059] shrink-0 mt-1"
                    />
                    <div>
                      <p className="font-serif text-lg text-[#0C2340]">
                        {b.title}
                      </p>
                      <p className="text-sm text-[#4A5568] mt-1 leading-relaxed">
                        {b.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/a-propos"
                data-testid="about-teaser-cta"
                className="mt-8 inline-flex items-center gap-2 text-[#0C2340] hover:text-[#C5A059] transition-colors"
              >
                Découvrir le parcours complet
                <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        data-testid="testimonials-section"
        className="section-fxv bg-[#0C2340] text-white grain"
      >
        <div className="container-fxv">
          <div className="max-w-3xl">
            <p className="overline">
              <span className="gold-line">Témoignages anonymes</span>
            </p>
            <h2 className="font-serif text-4xl md:text-5xl mt-6 text-white">
              Ce que disent les personnes accompagnées
            </h2>
            <p className="text-[#C9D4E3] mt-6 leading-relaxed">
              Les retours suivants sont anonymisés et publiés avec l'accord
              explicite des personnes concernées, conformément au cadre
              déontologique de la profession.
            </p>
          </div>

          <div className="mt-16 grid md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.author + t.text.slice(0, 12)} delay={i * 0.07}>
                <figure
                  data-testid={`testimonial-${i}`}
                  className="border border-[#1A3454] p-10 h-full"
                >
                  <Award size={22} strokeWidth={1.5} className="text-[#C5A059]" />
                  <blockquote className="font-serif text-xl leading-relaxed mt-6 text-white">
                    « {t.text} »
                  </blockquote>
                  <figcaption className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mt-6">
                    {t.author}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="section-fxv bg-white">
        <div className="container-fxv grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="overline gold-line">Questions fréquentes</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0C2340] mt-6 leading-tight">
              Vos questions, des réponses claires
            </h2>
            <p className="text-[#4A5568] mt-6 leading-relaxed">
              Une démarche thérapeutique se prépare. Voici les questions les
              plus fréquentes autour du déroulement, du cadre et de la pratique.
            </p>
            <Link
              to="/faq"
              data-testid="faq-see-all"
              className="mt-6 inline-flex items-center gap-2 text-[#0C2340] hover:text-[#C5A059] transition-colors"
            >
              Voir toutes les questions
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQ.slice(0, 5).map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`item-${i}`}
                  className="border-b border-[#E2E8F0]"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="text-left font-serif text-xl text-[#0C2340] hover:no-underline py-6"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#4A5568] leading-relaxed pb-6 text-base">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* BOOKING CTA */}
      <section
        data-testid="booking-section"
        className="section-fxv bg-[#F0F4F8]"
      >
        <div className="container-fxv">
          <div className="bg-white border border-[#E2E8F0] grid lg:grid-cols-12 overflow-hidden">
            <div className="lg:col-span-5 relative min-h-[300px]">
              <img
                src={IMAGES.hands}
                alt="Connexion humaine et soutien"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="lg:col-span-7 p-10 md:p-16">
              <p className="overline gold-line">Prendre rendez-vous</p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#0C2340] mt-6 leading-tight">
                Une première rencontre, sans engagement.
              </h2>
              <p className="text-[#4A5568] mt-6 leading-relaxed">
                Réservez en ligne via Doctolib, par téléphone, ou demandez à
                être rappelé. Vous serez accueilli dans un cadre confidentiel et
                bienveillant, en cabinet à Paris 16e ou en visioconsultation
                sécurisée.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={PRACTITIONER.doctolibUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booking-doctolib-btn"
                  className="rounded-full bg-[#0C2340] text-white px-7 py-3.5 text-sm font-medium hover:bg-[#163861] transition-colors inline-flex items-center gap-2"
                >
                  Réserver sur Doctolib
                  <ArrowRight size={16} strokeWidth={1.5} />
                </a>
                <Link
                  to="/contact"
                  data-testid="booking-form-btn"
                  className="rounded-full border border-[#C5A059] text-[#0C2340] px-7 py-3.5 text-sm font-medium hover:bg-[#C5A059] hover:text-white transition-colors"
                >
                  Formulaire de contact
                </Link>
                <a
                  href={`tel:${PRACTITIONER.phone}`}
                  data-testid="booking-call-btn"
                  className="rounded-full border border-[#E2E8F0] text-[#0C2340] px-7 py-3.5 text-sm font-medium hover:border-[#0C2340] transition-colors inline-flex items-center gap-2"
                >
                  <Phone size={16} strokeWidth={1.5} />
                  {PRACTITIONER.phoneDisplay}
                </a>
              </div>
              <div className="mt-8 flex items-center gap-6 text-xs text-[#4A5568]">
                <span className="inline-flex items-center gap-2">
                  <Video size={14} strokeWidth={1.5} className="text-[#C5A059]" />
                  Visioconsultation sécurisée
                </span>
                <span className="inline-flex items-center gap-2">
                  <Shield size={14} strokeWidth={1.5} className="text-[#C5A059]" />
                  Secret professionnel
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
